import { CSSProperties } from 'react'
import { IconButton } from '@mui/material'
import { CloudOffOutlined, CloudDownloadOutlined } from '@mui/icons-material'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import dayjs from 'dayjs'
import { GraphTooltip } from 'pages/DashboardPage/components/Graphs'
import EmptyGraphImage from 'assets/images/ui/loading-data.png'
import { ExpandIcon } from 'pages/DashboardPage/components/icons'
import { GraphDataPoint } from 'Hooks/useDashboard'
import Sty from 'modules/Sty'


/** Props for the SmallGraph component */
interface GraphProps {
  /** Unique index for the graph, used for gradient and positioning */
  index: number
  /** Function to handle graph expansion */
  expandGraph: () => void
  /** URL or path to the platform/asset image */
  imageAsset: string
  /** Main value to display (e.g. number of followers) */
  primaryValue: string | number
  /** Name of the platform (e.g. 'Spotify', 'YouTube') */
  platformName: string
  /** Subtitle text (e.g. 'Followers', 'Subscribers') */
  subtitle: string
  /** Array of data points for the graph */
  graphData?: GraphDataPoint[]
  /** Percentage change value */
  changeValue: number
  /** Whether to hide the change value when it's zero */
  hideChangeValueAtZero?: boolean
  /** Message to display when platform is decommissioned */
  decommissionMessage?: string
}

/** Component for displaying a small graph with platform statistics */
const SmallGraph = (props: GraphProps) => {
  const {
    index,
    expandGraph,
    imageAsset,
    primaryValue,
    platformName,
    subtitle,
    graphData,
    changeValue,
    hideChangeValueAtZero,
    decommissionMessage
  } = props

  const hasData = graphData && graphData.length > 0

  /** Unique ID for the graph gradient */
  const gradientId = `small-graph-gradient-${index}`
  /** Renders the change value percentage if conditions are met */
  const renderChangeValue = () => {
    if (!hasData) return null
    if (changeValue === 0 && hideChangeValueAtZero) return null

    return (
      <div className="changeValue w-auto">
        <p className={`text-lg md:text-xl font-semibold ${changeValue >= 0 ? 'text-green' : 'text-gray-500'}`}>
          {changeValue > 0 ? '+' : ''}{changeValue.toFixed(1) || 0}%
        </p>
      </div>
    )
  }

  /** Renders the gathering data or decommission message state */
  const renderGatheringState = () => {
    if (decommissionMessage) {
      return (
        <div className="decommissionMessage ml-2 text-left self-center">
          <div className="text-md font-medium flex items-center">
            <div className="mr-1"><CloudOffOutlined fontSize="small" /></div>
            {platformName} {subtitle}
          </div>
          <div className="text-xs">{decommissionMessage}</div>
        </div>
      )
    }
    
    return (
      <div className="stillGatheringData ml-2 text-left self-center">
        <div className="text-md font-medium flex items-center">
          <div className="mr-1"><CloudDownloadOutlined fontSize="small" /></div>
          {platformName} {subtitle}
        </div>
        <div className="text-xs">We're still gathering data... Check back tomorrow for updated stats!</div>
      </div>
    )
  }

  return (
    <div
      className="smallGraphComponent"
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
        flex: 1,
        border: Sty.border,
        borderRadius: 8,
      }}
    >
      <div className="mainInfoSmallGraphContainer flex w-full justify-between items-center pt-3 md:py-2 px-4">
        <div className="flex flex-row">
          <img
            className="smallGraphAssetImageIcon flex-shrink-0"
            width={50}
            src={imageAsset}
            alt={platformName}
          />
          {hasData ?
            <div className="hasDataContainer ml-2 text-left self-center">
              <div
                className="dataValueFollowerSubscriberCount text-xl font-medium"
                style={{ marginBottom: -4 }}
              >
                {primaryValue}
              </div>
              <div className="follwersSubscribersText text-sm text-gray-600">
                {subtitle}
              </div>
            </div>
            :
            renderGatheringState()
          }
        </div>
        {renderChangeValue()}
      </div>
      <div className="flex-1 flex flex-row justify-between items-center">
        {hasData ?
          <>
            <div className="smallGraphResponsiveContainerOuter w-full">
              <ResponsiveContainer
                width="100%"
                height={window.screen.width > 500 ? 100 : 80}
              >
                <AreaChart
                  data={graphData}
                  margin={{
                    top: 0,
                    right: 0,
                    left: 0,
                    bottom: 20,
                  }}
                >
                  <XAxis hide={true} dy={12} dataKey="date" />
                  <YAxis hide={true} domain={['auto', 'auto']} />
                  <Tooltip
                    position={window.innerWidth < 768 ? { y: -80 } : undefined}
                    allowEscapeViewBox={window.innerWidth < 768 ? { x: false, y: true } : undefined}
                    cursor={{ stroke: '#42c221', strokeWidth: 2 }}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <GraphTooltip
                            title={dayjs(label).format('dd MM/DD')}
                            value={payload[0].value!.toLocaleString('en')}
                          />
                        )
                      }
                      return null
                    }}
                  />
                  <defs>
                    <linearGradient
                      id={gradientId}
                      x1="0" y1="0" x2="0" y2="1"
                    >
                      <stop stopColor="#42c221" stopOpacity={0.4} />
                      <stop offset="1" stopColor="#42c221" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    fill={`url(#${gradientId})`}
                    strokeWidth={2}
                    dataKey="value"
                    stroke="#42c221"
                    fillOpacity="0.8"
                    activeDot={{ r: 8 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="smallGraphIconButton absolute right-0 bottom-0 mr-0.5 mb-0.5">
              <IconButton
                onClick={expandGraph}
                aria-label="Expand graph"
                data-intercom-target="GraphExpandButton"
                style={{
                  padding: 4
                }}
              >
                <ExpandIcon />
              </IconButton>
            </div>
          </>
          :
          <img
            className="emptyGraphImage w-full"
            src={EmptyGraphImage}
            draggable={false}
            alt="Empty Graph"
          />
        }
      </div>
    </div>
  )
}

export default SmallGraph